import {
  ApartmentOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  CloudSyncOutlined,
  ExperimentOutlined,
  FileTextOutlined,
  GlobalOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Button, Card, Col, Collapse, Divider, Image, Row, Tag, Typography } from "antd";
import AppImages from "assets/appImages";
import { motion } from "framer-motion";
import AppConfig from "utils/Config";

const { Title, Paragraph, Text } = Typography;

const diseasePillars = [
  {
    title: "National Priority Programs",
    tag: "High Throughput",
    desc: "Dedicated accessioning, batch workplans, and automated analyzer interfacing for Tuberculosis (TB GeneXpert) and HIV-1 Viral Load (VL) suppression monitoring.",
    icon: <ExperimentOutlined className="text-xl text-[#038fde]" />,
  },
  {
    title: "Surveillance & Early Detection",
    tag: "Public Health",
    desc: "Specimen collection and DNA PCR tracking for Early Infant Diagnosis (EID), alongside Environmental Surveillance (ESPN) for polio wastewater sampling.",
    icon: <SafetyCertificateOutlined className="text-xl text-[#377e21]" />,
  },
  {
    title: "Outbreak Response & Clinical Services",
    tag: "Epidemic Tracking",
    desc: "Rapid diagnostic tracking for national priority pathogens (Lassa Fever, Cholera, Mpox, Yellow Fever, COVID-19) and general hospital clinical investigations.",
    icon: <GlobalOutlined className="text-xl text-orange-600" />,
  },
];

const labSteps = [
  {
    step: "01",
    title: "Sample Reception",
    desc: "Specimen check-in, barcode generation, and requisition data validation.",
  },
  {
    step: "02",
    title: "Workplan & Testing",
    desc: "Batch creation, electronic worksheets, and laboratory analyzer processing.",
  },
  {
    step: "03",
    title: "Result Verification",
    desc: "Analyst test entry followed by supervisor quality review and authorization.",
  },
  {
    step: "04",
    title: "Dispatch & Sync",
    desc: "Immediate clinician reporting and bi-directional DHIS2/SORMAS national sync.",
  },
];

const verifiedFaqs = [
  {
    q: "How do facility users obtain an account or reset passwords?",
    a: "User accounts and password resets are managed locally by your designated Facility Administrator. For national system administration or facility onboarding, contact mdalhatu@gghnigeria.org.",
  },
  {
    q: "Which laboratory programs are currently supported?",
    a: "eN-LIS supports Tuberculosis (TB), HIV-1 Viral Load (VL), Early Infant Diagnosis (EID), Environmental Surveillance for Polio (ESPN), Public Health outbreak diseases, and General Clinical Laboratory investigations.",
  },
  {
    q: "How does eN-LIS ensure data quality and verification?",
    a: "The system enforces a strict two-tier verification workflow: primary measurements entered by an Analyst must be reviewed and electronically authorized by a Laboratory Supervisor before results can be approved or dispatched.",
  },
  {
    q: "Does the system integrate with existing health platforms?",
    a: "Yes. eN-LIS features bi-directional integration with DHIS2 for aggregate indicators, connects with SORMAS for epidemic outbreak notifications, and tracks courier consignments with NIPOST.",
  },
];

const LandingPage = () => {
  return (
    <div className="h-[100dvh] overflow-y-auto scroll-smooth bg-white text-gray-800 flex flex-col font-sans">
      {/* Top Navigation */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Image
              src={AppConfig.logo}
              alt={AppConfig.appName}
              preview={false}
              width={34}
            />
            <div className="flex items-center space-x-2">
              <span className="font-bold text-lg text-gray-900 tracking-tight">
                {AppConfig.appName}
              </span>
              <span className="hidden sm:inline-block text-xs text-gray-400 font-normal pl-2 border-l border-gray-200">
                National Laboratory Information System
              </span>
            </div>
          </div>

          <nav className="hidden md:flex items-center space-x-8 text-sm font-medium text-gray-600">
            <a href="#programs" className="hover:text-[#038fde] transition-colors">
              Programs
            </a>
            <a href="#workflow" className="hover:text-[#038fde] transition-colors">
              Workflow
            </a>
            <a href="#infrastructure" className="hover:text-[#038fde] transition-colors">
              Integrations
            </a>
            <a href="#faq" className="hover:text-[#038fde] transition-colors">
              FAQ
            </a>
            <a href="/docs" target="_blank" className="hover:text-[#038fde] transition-colors">
              Docs ↗
            </a>
          </nav>

          <div className="flex items-center">
            <Button
              type="primary"
              icon={<UserOutlined />}
              href="/signin"
              style={{
                backgroundColor: "#038fde",
                borderColor: "#038fde",
                borderRadius: 6,
                fontWeight: 500,
              }}
            >
              Sign In
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {/* Hero Section */}
        <section className="max-w-6xl mx-auto px-6 pt-16 pb-20 md:pt-24 md:pb-28">
          <Row gutter={[48, 48]} align="middle">
            <Col xs={24} md={13}>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="space-y-5"
              >
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-50 border border-green-200 text-xs font-semibold text-[#377e21]">
                  <span className="w-2 h-2 rounded-full bg-[#377e21]"></span>
                  Georgetown Global Health Nigeria & NCDC
                </div>

                <Title
                  level={1}
                  style={{
                    fontSize: "2.6rem",
                    fontWeight: 800,
                    lineHeight: 1.2,
                    letterSpacing: "-0.02em",
                    margin: 0,
                    color: "#0f172a",
                  }}
                >
                  Electronic National Laboratory Information System
                </Title>

                <Paragraph
                  style={{
                    fontSize: "1.1rem",
                    color: "#64748b",
                    lineHeight: 1.65,
                    margin: 0,
                    maxWidth: 540,
                  }}
                >
                  A unified digital platform modernizing diagnostic operations, sample
                  chain-of-custody, multi-tier result verification, and national
                  disease surveillance across Nigerian health facilities.
                </Paragraph>

                <div className="pt-2 flex flex-wrap items-center gap-3">
                  <Button
                    type="primary"
                    size="large"
                    icon={<ArrowRightOutlined />}
                    href="/signin"
                    style={{
                      backgroundColor: "#038fde",
                      borderColor: "#038fde",
                      borderRadius: 6,
                      fontWeight: 600,
                      height: 44,
                      padding: "0 24px",
                    }}
                  >
                    Launch Portal
                  </Button>
                  <Button
                    size="large"
                    icon={<FileTextOutlined />}
                    href="/docs"
                    target="_blank"
                    style={{
                      borderRadius: 6,
                      fontWeight: 500,
                      height: 44,
                      padding: "0 20px",
                    }}
                  >
                    Documentation
                  </Button>
                </div>

                <div className="pt-4 flex flex-wrap items-center gap-6 text-xs text-gray-500 font-medium">
                  <span className="flex items-center gap-1.5">
                    <CheckCircleOutlined className="text-[#377e21]" />
                    Two-Tier Result Validation
                  </span>
                  <span className="flex items-center gap-1.5">
                    <CheckCircleOutlined className="text-[#377e21]" />
                    DHIS2 & SORMAS Integration
                  </span>
                  <span className="flex items-center gap-1.5">
                    <CheckCircleOutlined className="text-[#377e21]" />
                    NDPR Data Compliant
                  </span>
                </div>
              </motion.div>
            </Col>

            <Col xs={24} md={11} className="text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: 0.1 }}
              >
                <img
                  src={AppImages.illustration}
                  alt="eN-LIS Laboratory Workflows"
                  className="w-full max-w-md mx-auto drop-shadow-sm"
                />
              </motion.div>
            </Col>
          </Row>
        </section>

        {/* Diagnostic Programs Section */}
        <section id="programs" className="bg-slate-50/70 border-y border-gray-100 py-20">
          <div className="max-w-6xl mx-auto px-6 space-y-12">
            <div className="text-center max-w-2xl mx-auto space-y-2">
              <span className="text-xs font-bold uppercase tracking-wider text-[#038fde]">
                Core Programs
              </span>
              <Title level={2} style={{ margin: 0, fontWeight: 700, color: "#0f172a" }}>
                Diagnostic & Disease Surveillance
              </Title>
              <Paragraph style={{ color: "#64748b", margin: 0, fontSize: "0.95rem" }}>
                Configured workflows supporting routine facility diagnostics and national public health priorities.
              </Paragraph>
            </div>

            <Row gutter={[24, 24]}>
              {diseasePillars.map((item, idx) => (
                <Col xs={24} md={8} key={idx}>
                  <Card
                    hoverable
                    style={{
                      borderRadius: 8,
                      border: "1px solid #e2e8f0",
                      height: "100%",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                    }}
                    bodyStyle={{ padding: 28 }}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-10 h-10 rounded-md bg-white border border-gray-200 flex items-center justify-center">
                        {item.icon}
                      </div>
                      <Tag color="blue" style={{ borderRadius: 4, margin: 0 }}>
                        {item.tag}
                      </Tag>
                    </div>
                    <Title level={4} style={{ marginBottom: 8, fontWeight: 600, color: "#0f172a" }}>
                      {item.title}
                    </Title>
                    <Paragraph style={{ color: "#64748b", fontSize: "0.875rem", lineHeight: 1.6, margin: 0 }}>
                      {item.desc}
                    </Paragraph>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </section>

        {/* Standard Laboratory Workflow */}
        <section id="workflow" className="py-20 max-w-6xl mx-auto px-6 space-y-12">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <span className="text-xs font-bold uppercase tracking-wider text-[#377e21]">
              Operational Lifecycle
            </span>
            <Title level={2} style={{ margin: 0, fontWeight: 700, color: "#0f172a" }}>
              Standard Laboratory Workflow
            </Title>
            <Paragraph style={{ color: "#64748b", margin: 0, fontSize: "0.95rem" }}>
              A structured 4-step chain-of-custody ensuring specimen traceability and quality validation.
            </Paragraph>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {labSteps.map((s, idx) => (
              <div
                key={idx}
                className="bg-white p-6 rounded-lg border border-gray-200/80 shadow-xs space-y-2 relative"
              >
                <span className="text-2xl font-black text-gray-200 block font-mono">
                  {s.step}
                </span>
                <Text strong style={{ fontSize: "1rem", color: "#0f172a", display: "block" }}>
                  {s.title}
                </Text>
                <p className="text-xs text-gray-500 leading-relaxed m-0">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Infrastructure & Governance Split */}
        <section id="infrastructure" className="bg-slate-50/70 border-y border-gray-100 py-20">
          <div className="max-w-6xl mx-auto px-6">
            <Row gutter={[48, 48]} align="middle">
              <Col xs={24} md={12} className="space-y-4">
                <span className="text-xs font-bold uppercase tracking-wider text-[#038fde]">
                  Interoperability
                </span>
                <Title level={3} style={{ margin: 0, fontWeight: 700, color: "#0f172a" }}>
                  Connected to National Health Infrastructure
                </Title>
                <Paragraph style={{ color: "#64748b", lineHeight: 1.7, fontSize: "0.95rem", margin: 0 }}>
                  eN-LIS is built to operate within Nigeria&apos;s national eHealth architecture.
                  The platform provides automated data sync with DHIS2 for aggregate health indicators,
                  connects directly with SORMAS for epidemic outbreak tracking, and integrates with NIPOST logistics.
                </Paragraph>
                <div className="pt-2 flex flex-wrap gap-2">
                  <Tag color="cyan" style={{ borderRadius: 4, padding: "2px 8px" }}>
                    <CloudSyncOutlined /> DHIS2 Integration
                  </Tag>
                  <Tag color="purple" style={{ borderRadius: 4, padding: "2px 8px" }}>
                    <GlobalOutlined /> SORMAS Outbreak Sync
                  </Tag>
                  <Tag color="green" style={{ borderRadius: 4, padding: "2px 8px" }}>
                    <ApartmentOutlined /> NIPOST Logistics
                  </Tag>
                </div>
              </Col>

              <Col xs={24} md={12}>
                <Card
                  style={{
                    borderRadius: 8,
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                  }}
                  bodyStyle={{ padding: 24 }}
                >
                  <div className="space-y-3">
                    <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                      <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Role-Based Access
                      </span>
                      <span className="text-xs text-[#038fde] font-semibold">Active Profiles</span>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div className="p-2.5 rounded bg-white border border-gray-100">
                        <span className="font-semibold text-gray-800 block">SuperAdmin</span>
                        <span className="text-gray-400">System governance & registry</span>
                      </div>
                      <div className="p-2.5 rounded bg-white border border-gray-100">
                        <span className="font-semibold text-gray-800 block">Facility Admin</span>
                        <span className="text-gray-400">User roster & test menus</span>
                      </div>
                      <div className="p-2.5 rounded bg-white border border-gray-100">
                        <span className="font-semibold text-gray-800 block">Supervisor</span>
                        <span className="text-gray-400">QC & result validation</span>
                      </div>
                      <div className="p-2.5 rounded bg-white border border-gray-100">
                        <span className="font-semibold text-gray-800 block">Analyst</span>
                        <span className="text-gray-400">Testing & worksheet entry</span>
                      </div>
                      <div className="p-2.5 rounded bg-white border border-gray-100">
                        <span className="font-semibold text-gray-800 block">Receptionist</span>
                        <span className="text-gray-400">Sample receipt & barcode</span>
                      </div>
                      <div className="p-2.5 rounded bg-white border border-gray-100">
                        <span className="font-semibold text-gray-800 block">Data Analyst</span>
                        <span className="text-gray-400">Indicators & epidemiology</span>
                      </div>
                    </div>
                  </div>
                </Card>
              </Col>
            </Row>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="max-w-4xl mx-auto px-6 py-20 space-y-8">
          <div className="text-center space-y-2">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-500">
              Guidance
            </span>
            <Title level={2} style={{ margin: 0, fontWeight: 700, color: "#0f172a" }}>
              Frequently Asked Questions
            </Title>
            <Paragraph style={{ color: "#64748b", margin: 0, fontSize: "0.95rem" }}>
              Essential operating information for facilities and administrative teams.
            </Paragraph>
          </div>

          <Collapse
            accordion
            bordered={false}
            defaultActiveKey={["0"]}
            style={{ backgroundColor: "transparent" }}
          >
            {verifiedFaqs.map((faq, idx) => (
              <Collapse.Panel
                header={
                  <span className="font-semibold text-gray-800 text-sm">{faq.q}</span>
                }
                key={String(idx)}
                style={{
                  marginBottom: 12,
                  backgroundColor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderRadius: 6,
                  overflow: "hidden",
                }}
              >
                <p className="text-xs text-gray-600 leading-relaxed m-0">
                  {faq.a}
                </p>
              </Collapse.Panel>
            ))}
          </Collapse>
        </section>
      </main>

      {/* Clean Minimal Footer */}
      <footer className="border-t border-gray-100 bg-white py-12 px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 text-xs text-gray-500">
          <div className="flex items-center space-x-3">
            <Image
              src={AppConfig.logo}
              alt={AppConfig.appName}
              preview={false}
              width={26}
            />
            <span className="font-bold text-gray-800 text-sm">
              {AppConfig.appName}
            </span>
            <span className="text-gray-400 pl-2 border-l border-gray-200">
              Georgetown Global Health Nigeria (GGHN) & NCDC
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-6">
            <a href="/signin" className="hover:text-[#038fde] transition-colors">
              Launch Portal
            </a>
            <a href="/docs" target="_blank" className="hover:text-[#038fde] transition-colors">
              Documentation
            </a>
            <a href="/terms" className="hover:text-[#038fde] transition-colors">
              Terms of Service
            </a>
            <a href="mailto:support@en-lis.com" className="hover:text-[#038fde] transition-colors">
              support@en-lis.com
            </a>
          </div>
        </div>

        <Divider style={{ margin: "20px 0" }} />

        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between text-xs text-gray-400">
          <p className="m-0">
            &copy; {new Date().getFullYear()} eN-LIS. All rights reserved.
          </p>
          <p className="m-0 mt-2 sm:mt-0">
            Compliant with NDPR & National Health Data Standards
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
